// The notes panel of a chart's "notes" event layer: the notes on this chart,
// "Add a note" for a caller who may, and Edit / Delete on the caller's own
// (an admin's on any). Rendered by InsightVisual in a portal next to the
// chart's "+ Note" chip; talks to the host only through the notes transport.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { displayDate } from './overlay'
import type { EventItem, EventLayer } from './model'
import { EVENTS_CONFIG } from './model'
import type { VisualNotesTransport } from './notesContext'

export interface NotesPanelProps {
  anchor: DOMRect
  layer: EventLayer
  notes: EventItem[]
  canAdd: boolean
  admin?: boolean
  project?: string
  objName?: string
  transport: VisualNotesTransport | null
  /** Pre-fills the date (a date clicked on the chart). */
  initialDate?: string
  onNotes: (notes: EventItem[], canAdd?: boolean, admin?: boolean) => void
  onClose: () => void
}

const today = () => new Date().toISOString().slice(0, 10)

const box: CSSProperties = {
  position: 'fixed', zIndex: 2000, width: 340, maxWidth: 'calc(100vw - 24px)',
  overflow: 'auto', background: 'var(--color-bg-secondary, #ffffff)', color: 'var(--color-text, #1b2330)',
  border: '1px solid var(--color-border, #dfe3ea)', borderRadius: 10, boxShadow: '0 12px 32px rgba(16,24,40,.22)',
  padding: 14, fontSize: 14,
}
const inp: CSSProperties = {
  font: 'inherit', fontSize: 14, width: '100%', boxSizing: 'border-box', minHeight: 34, padding: '6px 9px',
  border: '1px solid var(--color-border-strong, #c9d1db)', borderRadius: 6,
  background: 'var(--color-input-bg, #ffffff)', color: 'var(--color-text, #1b2330)',
}
const lbl: CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary, #465062)', marginBottom: 4 }
const btn: CSSProperties = {
  font: 'inherit', fontSize: 14, fontWeight: 500, borderRadius: 6, padding: '6px 12px', minHeight: 34, cursor: 'pointer',
  border: '1px solid var(--color-border-strong, #c9d1db)', background: 'var(--color-secondary-btn, #f0f3f7)',
  color: 'var(--color-text, #1b2330)',
}
const primary: CSSProperties = { ...btn, background: 'var(--color-accent, #0061D0)', borderColor: 'var(--color-accent, #0061D0)', color: '#fff' }

interface Draft { id?: number; date: string; end: string; label: string; note: string }

function errText(e: unknown): string {
  const any = e as { response?: { data?: { detail?: unknown } }; message?: string; detail?: unknown }
  const d = any?.response?.data?.detail ?? any?.detail
  if (d && typeof d === 'object' && 'message' in (d as object)) return String((d as { message: unknown }).message)
  if (typeof d === 'string') return d
  return any?.message || 'The note could not be saved.'
}

export function NotesPanel(p: NotesPanelProps) {
  const [draft, setDraft] = useState<Draft | null>(
    p.canAdd ? { date: p.initialDate || today(), end: '', label: '', note: '' } : null,
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const lim = EVENTS_CONFIG.limits

  // Close on Escape / a click elsewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') p.onClose() }
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) p.onClose()
    }
    document.addEventListener('keydown', onKey)
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); clearTimeout(t) }
  }, [p])

  // Fixed to the viewport, so it must FIT it: below the "+ Note" chip when
  // there is room, else as high as it needs, scrolling inside when the window
  // is short — a Save button below the fold cannot be reached at all.
  const pos = useMemo<CSSProperties>(() => {
    const w = 340
    const want = 420
    const vh = window.innerHeight
    const left = Math.max(12, Math.min(p.anchor.left, window.innerWidth - w - 12))
    const top = Math.max(12, Math.min(p.anchor.bottom + 6, vh - want - 12))
    return { left, top, maxHeight: vh - top - 12 }
  }, [p.anchor])

  const call = async (op: 'add' | 'update' | 'delete', extra: Record<string, unknown>) => {
    if (!p.transport) { setError('Notes cannot be saved here.'); return false }
    setBusy(true)
    setError(null)
    try {
      const ans = await p.transport(op, { project: p.project, obj_name: p.objName, layer: p.layer.id, ...extra })
      p.onNotes(ans?.notes ?? [], ans?.can_add, ans?.admin)
      return true
    } catch (e) {
      console.warn('[visual-events] notes', op, 'failed', e)
      setError(errText(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    if (!draft) return
    if (!draft.label.trim()) { setError('Say what happened.'); return }
    if (!draft.date) { setError('Pick the date the note is about.'); return }
    if (draft.end && draft.end < draft.date) { setError('The end date is before the start date.'); return }
    const ok = await call(draft.id ? 'update' : 'add', {
      id: draft.id, date: draft.date, end: draft.end || null, label: draft.label.trim(), note: draft.note.trim(),
    })
    if (ok) setDraft(p.canAdd ? { date: draft.date, end: '', label: '', note: '' } : null)
  }

  return createPortal(
    <div ref={ref} role="dialog" aria-label={`${p.layer.name} on this chart`} data-tw="visual-notes-panel" style={{ ...box, ...pos }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ width: 12, height: 12, borderRadius: 3, background: p.layer.color }} />
        <div style={{ fontWeight: 600, fontSize: 15, flex: 1 }}>{p.layer.name}</div>
        <button type="button" aria-label="Close" onClick={p.onClose} style={{ ...btn, minHeight: 28, padding: '2px 8px', background: 'transparent', border: 0 }}>✕</button>
      </div>

      {draft && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={lbl} htmlFor="tw-note-date">Date</label>
              <input id="tw-note-date" type="date" style={inp} value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={lbl} htmlFor="tw-note-end">Until (optional)</label>
              <input id="tw-note-end" type="date" style={inp} value={draft.end}
                onChange={(e) => setDraft({ ...draft, end: e.target.value })} />
            </div>
          </div>
          <label style={lbl} htmlFor="tw-note-label">What happened</label>
          <input id="tw-note-label" type="text" style={{ ...inp, marginBottom: 8 }} maxLength={lim.label_max}
            placeholder="For example Warehouse outage" value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
          <label style={lbl} htmlFor="tw-note-text">Note</label>
          <textarea id="tw-note-text" rows={2} style={{ ...inp, resize: 'vertical' }} maxLength={lim.text_max}
            value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary, #465062)', margin: '6px 0 10px' }}>
            Seen by everyone in your organization who can see this chart.
          </div>
          {error && <div role="alert" style={{ color: 'var(--color-error, #b42318)', fontSize: 13, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {draft.id != null && (
              <button type="button" style={btn} disabled={busy}
                onClick={() => setDraft(p.canAdd ? { date: draft.date, end: '', label: '', note: '' } : null)}>Cancel</button>
            )}
            <button type="button" style={primary} disabled={busy} data-tw="visual-note-save" onClick={() => void save()}>
              {busy ? 'Saving…' : draft.id != null ? 'Save changes' : 'Save note'}
            </button>
          </div>
        </div>
      )}
      {!draft && error && <div role="alert" style={{ color: 'var(--color-error, #b42318)', fontSize: 13, marginBottom: 8 }}>{error}</div>}

      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary, #465062)', margin: '4px 0 6px' }}>
        On this chart ({p.notes.length})
      </div>
      {p.notes.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary, #465062)' }}>No notes yet.</div>
      )}
      {p.notes.map((n) => {
        const mayChange = !!n.mine || !!p.admin
        return (
          <div key={String(n.id ?? n.date + n.label)} style={{ borderTop: '1px solid var(--color-border, #eef1f5)', padding: '8px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ fontWeight: 600, flex: 1, minWidth: 0 }}>{n.label}</div>
              {mayChange && n.id != null && (
                <>
                  <button type="button" style={{ ...btn, minHeight: 26, padding: '1px 8px', fontSize: 13 }} disabled={busy}
                    onClick={() => setDraft({ id: n.id, date: n.date.slice(0, 10), end: (n.end || '').slice(0, 10), label: n.label, note: n.note || '' })}>Edit</button>
                  <button type="button" style={{ ...btn, minHeight: 26, padding: '1px 8px', fontSize: 13, color: 'var(--color-error, #b42318)' }} disabled={busy}
                    onClick={() => void call('delete', { id: n.id })}>Delete</button>
                </>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary, #465062)' }}>
              {displayDate(n.date)}{n.end ? ` → ${displayDate(n.end)}` : ''}{n.by_name ? ` · ${n.mine ? 'You' : n.by_name}` : ''}
            </div>
            {n.note && <div style={{ fontSize: 14, marginTop: 2 }}>{n.note}</div>}
          </div>
        )
      })}
    </div>,
    document.body,
  )
}

export default NotesPanel
